import { ProductQuality } from "@/Apollo/schema";

/**
 * Open Home domain model, shared by the API routes and the UI.
 *
 * A *home* belongs to one seller and holds up to five *rooms*. A room has photos; recognition
 * turns each photo into *items*. An item is not a listing: it has an optional price and an optional
 * minimum offer, and it becomes purchasable only after an accepted offer (or the price taken) *and*
 * the seller has filled in the same details a normal ad asks for (dimensions, weight, pickup,
 * condition).
 */

export const MAX_ROOMS_PER_SELLER = 10;
export const MAX_PHOTOS_PER_ROOM = 6;
/** Offers lapse after seven days, an accepted deal after 48 hours without a sale (plan slide 7). */
export const OFFER_TTL_HOURS = 24 * 7;
export const RESERVATION_TTL_HOURS = 48;
export const MAX_OPEN_OFFERS_PER_BUYER = 10;

/** Same commission as a normal listing: 12.5% incl. VAT for individuals, 16% excl. VAT for business. */
export const COMMISSION = { individual: 0.125, business: 0.16 } as const;

/** The six sites. A seller writes in their own language; buyers read theirs. */
export const OPEN_HOME_LANGS = ["en", "nl", "de", "fr", "es", "it"] as const;
export type OpenHomeLang = (typeof OPEN_HOME_LANGS)[number];

export const LANGUAGE_NAMES: Record<OpenHomeLang, string> = {
  en: "English",
  nl: "Dutch",
  de: "German",
  fr: "French",
  es: "Spanish",
  it: "Italian",
};

export const asLang = (value: string | null | undefined): OpenHomeLang | null =>
  OPEN_HOME_LANGS.find(lang => lang === value?.toLowerCase().slice(0, 2)) ??
  null;

/**
 * A title per language. English is always there (recognition writes it, matching uses it);
 * the seller's own language is added next to it, other languages come from the platform's
 * listing translation (gateway) when a buyer reads them.
 */
export type LocalizedTitle = { en: string } & Partial<
  Record<Exclude<OpenHomeLang, "en">, string>
>;

/** The title in `locale`, else English; empty when there is no title. */
export const titleIn = (
  title: LocalizedTitle | null | undefined,
  locale: string | null | undefined
) => {
  const lang = asLang(locale);

  return title ? (lang && title[lang]) || title.en : "";
};

export type Box = { top: number; left: number; bottom: number; right: number }; // 0..1

export type Confidence = number; // 0..1

export type ItemStatus =
  | "suggested" // found by recognition, not yet reviewed
  | "off" // owner switched it off (not for sale)
  | "approved" // owner confirmed, visible to logged-in buyers
  | "reserved" // offer accepted, waiting for the buyer to pay
  | "sold"
  | "gone"; // sold elsewhere / removed by owner

/** The same three levels a normal listing uses on its PDP (`QUALITY_MAP`). */
export type Condition = ProductQuality;

export const CONDITIONS: Condition[] = [
  ProductQuality.Perfect,
  ProductQuality.Great,
  ProductQuality.Good,
];

export type BrandClaim = {
  /** What the model read or recognised, verbatim. */
  raw: string | null;
  /** Our brand row, when the claim projects onto one. */
  brandId: string | null;
  title: string | null;
  slug: string | null;
  designer: string | null;
  model: string | null;
  confidence: Confidence;
  evidence: "label" | "design" | "none";
  /** Listings in our own catalogue that agree with the claim (the "Match" step). */
  catalogueHits: number;
  /** Median asking price of those hits, EUR, shown to the owner only as a floor hint. */
  medianPrice: number | null;
  replicaRisk: boolean;
  /** Why the model named it (identify pass) and what else it could be: for the back-office queue. */
  reasoning?: string | null;
  alternative?: string | null;
  /** Outcome of the three-way confirm step, when it ran. */
  confirmed?: "named" | "alternative" | "generic" | null;
};

export type Item = {
  id: string;
  roomId: string;
  photoId: string;
  /** Other photos in the same room this item was merged from. */
  alsoIn: string[];
  /**
   * Where the piece was also seen, one box per other photo (cross-photo de-duplication). Lives in
   * the recognition JSON only; `photoId` + `box` stay the primary (best) view.
   */
  seenIn?: { photoId: string; box: Box }[];
  box: Box;
  status: ItemStatus;
  /** Brand-neutral title: what buyers see until the owner confirms the brand. */
  title: LocalizedTitle;
  /** Title with maker and model, used once `brandConfirmed`. Null when nothing was identified. */
  brandedTitle: LocalizedTitle | null;
  /** The seller's language when recognised: the only title they see and edit. */
  titleLocale?: OpenHomeLang;
  objectType: string;
  categorySlug: string | null;
  categoryPath: string | null;
  categoryConfidence: Confidence;
  materials: string[];
  colors: string[];
  styles: string[];
  era: string | null;
  estimatedSize: string | null;
  brand: BrandClaim;
  /** Owner confirmed brand/designer; only then is it shown to buyers. */
  brandConfirmed: boolean;
  /** Owner answered "I don't know": the suggestion stays private and is never shown. */
  brandUnsure?: boolean;
  /** Field-level gate: the fields the owner has to look at before approving. */
  amber: string[];
  whoppahWorthy: boolean;
  condition: Condition | null;
  conditionNote: string | null;
  /** Minimum offer: shown to buyers, offers below it are not possible (Thomas, 10-03). */
  floorEur: number | null;
  /**
   * The owner's price, optional (Thomas, 10-07). A member who takes it has the piece at once; the
   * owner then adds the details and the buyer checks out, never straight from the page.
   */
  buyNowEur: number | null;
  /** Offers below the price on or off, per piece. Off needs a price. */
  allowOffers: boolean;
  /** Filled after an accepted offer, same fields as create-ad. */
  listingDetails: ListingDetails | null;
  confirmedAt: string | null;
  /**
   * Sold-price range from comparable Whoppah sales (owner only). Undefined when not asked for, so
   * the recognition's median hint stays; null when the gateway has none.
   */
  priceRange?: PriceRange | null;
  /** Owner edits, one per field, which are the labels the plan uses to measure recognition. */
  corrections: string[];
};

export type PriceRange = {
  lowEur: number;
  expectedEur: number;
  highEur: number;
  sales: number;
  /** A known condition would narrow the range: nudge the owner to add it. */
  conditionDependent: boolean;
};

export type ListingDetails = {
  widthCm: number;
  depthCm: number;
  heightCm: number;
  weightKg: number;
  pickupAllowed: boolean;
  courierAllowed: boolean;
  condition: Condition;
  conditionNote: string;
  quantity: number;
  confirmedAt: string;
};

export type Photo = {
  id: string;
  /**
   * Gateway-relative path of the privacy-processed image (EXIF stripped, sensitive regions blurred).
   * Never sent to the browser as a link: images go through `/api/open-home?p=image/...`.
   */
  path: string;
  width: number;
  height: number;
  blurredRegions: { kind: string; box: Box }[];
};

export type RecognitionRun = {
  photoId: string;
  model: string;
  ms: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  items: number;
  verified: number;
  cacheHits: number;
  /** True when the thorough multi-tile privacy pass ran (auto-triggered by a reflective surface). */
  thoroughPrivacy?: boolean;
};

/**
 * Why the safety scan refused a room photo (`server/photo-scan.ts`, Thomas 10-27). The API sends
 * it next to the error, the owner reads the matching sentence (`copy.ts`) and `oh_photo_blocked`
 * carries it as `oh_reason`. Listed from the most to the least serious: a photo that breaks two
 * rules is refused for the first. `unsafe` is a photo the model itself would not look at.
 */
export const PHOTO_REFUSAL_REASONS = [
  "sexual",
  "violence",
  "hate",
  "weapons",
  "drugs",
  "illegal_goods",
  "prompt_injection",
  "unsafe",
] as const;
export type PhotoRefusalReason = (typeof PHOTO_REFUSAL_REASONS)[number];

export const isPhotoRefusalReason = (
  value: unknown
): value is PhotoRefusalReason =>
  PHOTO_REFUSAL_REASONS.some(reason => reason === value);

export type Room = {
  id: string;
  sellerId: string;
  name: string;
  /** Seller's profile location at upload time; never an address. */
  city: string | null;
  country: string | null;
  photos: Photo[];
  items: Item[];
  runs: RecognitionRun[];
  paused: boolean;
  published: boolean;
  createdAt: string;
  updatedAt: string;
};

export type OfferStatus =
  | "open"
  | "declined"
  | "auto-declined"
  | "countered"
  | "accepted"
  | "awaiting-details"
  | "ready" // details confirmed: Buy now / checkout unlocked
  | "paid"
  | "expired"
  | "withdrawn";

export type Offer = {
  id: string;
  itemId: string;
  roomId: string;
  sellerId: string;
  buyerId: string;
  /** The buyer's merchant: what a gateway Bid references (Bid.buyer_id). */
  buyerMerchantId?: string | null;
  amountEur: number;
  counterEur: number | null;
  note: string;
  wantsPickup: boolean;
  status: OfferStatus;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  /** Set once the deal is handed to the normal flow (gateway createOpenHomeDeal). */
  gatewayProductId?: string | null;
  gatewayProductSlug?: string | null;
  dealError?: string | null;
  /** Gateway backend: the chat the bid opened, as with any Whoppah bid. */
  threadId?: string | null;
};

/** What a buyer is allowed to see of a room: no seller id and nothing that is not for sale. */
export type PublicItem = Pick<
  Item,
  | "id"
  | "roomId"
  | "photoId"
  | "box"
  | "title"
  | "objectType"
  | "categoryPath"
  | "categorySlug"
  | "materials"
  | "colors"
  | "styles"
  | "era"
  | "estimatedSize"
  | "condition"
  | "conditionNote"
> & {
  status: "approved" | "reserved" | "sold";
  brandTitle: string | null;
  designer: string | null;
  model: string | null;
  /** The owner's minimum offer, public since 10-03. */
  minimumOfferEur: number | null;
  /** The owner's price, when set. */
  buyNowEur: number | null;
  allowOffers: boolean;
};

/**
 * What a buyer gets of a room. Never where the owner lives, not even the city: next to photos of
 * the inside of a home, that helps someone find it (launch promise, 2026-10-27).
 */
export type PublicRoom = {
  id: string;
  name: string;
  photos: Pick<Photo, "id" | "width" | "height">[];
  items: PublicItem[];
  /**
   * Explore only: the same for every room of one owner within one response, and nothing
   * outside it ("More rooms from this seller").
   */
  sellerKey?: string;
  /** Explore only: the viewer's own room ("Your home"). */
  own?: boolean;
};

/** What a buyer or seller does with an offer (`/api/open-home?p=offers/<id>`). */
export type OfferAction =
  | { action: "accept" }
  | { action: "decline" }
  | { action: "counter"; amountEur: number }
  | { action: "withdraw" }
  | { action: "details"; details: Omit<ListingDetails, "confirmedAt"> }
  | { action: "pay"; delivery: "pickup" | "courier" };
